import { forwardRef } from 'react';

import {
  VENDOR_SHEET_FIELDS,
  matchVendorSheetCaption,
  type CloudeqEntity,
  type ExecuteVendorImportResponse,
  type PlanVendorImportResponse,
  type VendorImportBatch,
} from '@clm/shared-types';

import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Tile } from '../quality/panel';
import {
  CHECK_FIELD_COPY,
  ENTITY_SOURCE_COPY,
  FIELD_LABEL,
  FR29_NO_CHANGE_COPY,
  INDIVIDUAL_TEST_COLUMN_COPY,
  OUTCOME_COPY,
  SHEET_STATE_COPY,
  SOURCE_SYSTEM_COPY,
  TILE_COPY,
  TILE_ORDER,
  entityName,
  rowLabel,
} from '../vendorImportCopy';

/**
 * The read-only pieces of the **Vendor list** surface — CLM-113 + CLM-114
 * (§4.7): the summary tiles, the Columns panel, the "not in this sheet" list,
 * the receipt and the history. Each renders wire values through the copy
 * tables only; none ever shows a cell value from an ignored or check-only
 * column, because the plan never carries one (R-113-4).
 */

export function PlanSummary({ plan }: { readonly plan: PlanVendorImportResponse }) {
  const file = plan.file;
  return (
    <>
      <div className="totals" aria-label="What this sheet would do">
        {TILE_ORDER.map((tile) => (
          <Tile
            key={tile}
            label={TILE_COPY[tile]}
            value={plan.totals[tile]}
            tone={tile === 'decision_required' && plan.totals[tile] > 0 ? 'warn' : undefined}
          />
        ))}
      </div>
      <p>
        <strong>Not in this sheet: {plan.totals.not_in_sheet}</strong> — nothing is deactivated.
      </p>
      <p className="mut">
        {file.sheet_name === null ? 'A CSV file' : <>Sheet “{file.sheet_name}”</>} · {file.data_row_count} data{' '}
        {file.data_row_count === 1 ? 'row' : 'rows'} · header found on {rowLabel(file.header_row_ref)}.{' '}
        {SOURCE_SYSTEM_COPY[plan.source_system]}
      </p>
      {file.sheet_state !== null && file.sheet_state !== 'visible' ? <p className="mut">{SHEET_STATE_COPY[file.sheet_state]}</p> : null}
      {file.other_sheets_ignored > 0 ? (
        <p className="mut">
          {file.other_sheets_ignored} other {file.other_sheets_ignored === 1 ? 'sheet was' : 'sheets were'} ignored; only
          the first sheet is read.
        </p>
      ) : null}
    </>
  );
}

/**
 * The Columns panel: each imported field beside the caption it came from, the
 * four check-only columns with what each decides, and the ignored captions
 * collapsed under their count. Captions only — never a value.
 */
export function ColumnsPanel({ plan }: { readonly plan: PlanVendorImportResponse }) {
  const captionOf = new Map(plan.columns.mapped.map((column) => [column.field, column.caption]));
  const ignored = plan.columns.ignored;
  return (
    <section className="vi-columns" aria-labelledby="vi-columns-heading">
      <h4 id="vi-columns-heading">Columns</h4>
      <div className="vi-columns-grid">
        <div>
          <h5>Read</h5>
          <ul aria-label="Columns read">
            {VENDOR_SHEET_FIELDS.map((field) => {
              const caption = captionOf.get(field);
              return (
                <li key={field}>
                  {FIELD_LABEL[field]} ←{' '}
                  {caption === undefined ? <span className="mut">not in this sheet</span> : <code>{caption}</code>}
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <h5>Read to decide, never stored</h5>
          {plan.columns.checked_only.length === 0 ? (
            <p className="mut">None of the four check columns is in this sheet.</p>
          ) : (
            <ul aria-label="Columns read to decide, never stored">
              {plan.columns.checked_only.map((caption) => {
                const match = matchVendorSheetCaption(caption);
                return (
                  <li key={caption}>
                    <code>{caption}</code>
                    {match?.kind === 'check_only' ? <span className="mut"> — {CHECK_FIELD_COPY[match.field]}</span> : null}
                  </li>
                );
              })}
            </ul>
          )}
          {plan.individual_test_columns.length > 0 ? (
            <p className="mut">
              Individuals are recognised here by{' '}
              {plan.individual_test_columns.map((column) => INDIVIDUAL_TEST_COLUMN_COPY[column]).join(', ')}.
            </p>
          ) : (
            <p className="mut">This sheet has no column that marks an individual, so every row reads as an organisation.</p>
          )}
        </div>
      </div>
      <details className="vi-ignored">
        <summary>
          {ignored.length} {ignored.length === 1 ? 'column' : 'columns'} ignored — contact details, tax ids and
          accounting fields are never imported
        </summary>
        <ul aria-label="Columns ignored">
          {ignored.map((caption, index) => (
            <li key={`${caption}-${String(index)}`}>
              <code>{caption}</code>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

export function NotInSheet({ plan }: { readonly plan: PlanVendorImportResponse }) {
  if (plan.not_in_sheet.length === 0) return null;
  return (
    <details className="vi-not-in-sheet">
      <summary>
        Not in this sheet: {plan.not_in_sheet.length} {plan.not_in_sheet.length === 1 ? 'vendor' : 'vendors'} imported
        earlier for this company — nothing is deactivated
      </summary>
      <p className="mut">
        A vendor missing from a newer sheet stays as it is. Deactivating one is an explicit act, never an omission
        from a spreadsheet.
      </p>
      <ul>
        {plan.not_in_sheet.map((entry) => (
          <li key={`${entry.party_key}-${entry.vendor_no}`}>
            {entry.display_name ?? <span className="mut">An individual — not shown</span>} (<code>{entry.party_key}</code>) ·
            vendor no. <code>{entry.vendor_no}</code> · last
            imported in batch #{entry.last_import_batch_id}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The receipt: counts, the batch id, the keys created, each name now shared
 * with another party, and the FR-29 notice (R-114-6). The notice follows the
 * **unscoped** party count, never the scoped contract count, so a
 * practice-scoped Admin is never told "no map changes" while contracts outside
 * their scope drift.
 */
export const Receipt = forwardRef<
  HTMLHeadingElement,
  {
    readonly receipt: ExecuteVendorImportResponse;
    readonly plan: PlanVendorImportResponse;
    readonly entities: readonly CloudeqEntity[];
  }
>(function Receipt({ receipt, plan, entities }, ref) {
  const batch = receipt.batch;
  const created = receipt.rows.filter((row) => row.outcome === 'created' && row.party_key !== null);
  const shared = receipt.rows.flatMap((row) => row.shared_aliases.map((alias) => ({ row_ref: row.row_ref, ...alias })));
  const individual = new Set(plan.rows.filter((row) => row.is_individual).map((row) => row.row_ref));
  const nameOf = (rowRef: string): string | null =>
    individual.has(rowRef) ? null : (plan.rows.find((row) => row.row_ref === rowRef)?.display_name ?? null);
  const fr29 = receipt.fr29;

  return (
    <section className="vi-receipt" aria-live="polite">
      <h4 ref={ref} tabIndex={-1}>
        Import receipt
      </h4>
      {batch === null ? (
        <p>
          <strong>Nothing was written.</strong> Every approved row was already as the sheet says, so no batch, party,
          party alias or reference was recorded.
        </p>
      ) : (
        <>
          <p>
            Batch <strong>#{batch.import_batch_id}</strong> under {entityName(entities, batch.cloudeq_entity_key)} (
            {ENTITY_SOURCE_COPY[batch.entity_source]}) · audit row <code>{receipt.audit?.audit_id}</code>
          </p>
          <p className="mut" data-testid="vi-receipt-counts">
            {batch.rows_created} {OUTCOME_COPY.created} · {batch.rows_linked} {OUTCOME_COPY.linked} · {batch.rows_updated}{' '}
            {OUTCOME_COPY.updated} · {batch.rows_unchanged} {OUTCOME_COPY.unchanged} · {batch.rows_skipped}{' '}
            {OUTCOME_COPY.skipped} · {batch.rows_rejected} {OUTCOME_COPY.rejected} · {batch.aliases_added} party aliases
            added
          </p>
        </>
      )}

      {created.length > 0 ? (
        <details open={created.length <= 20}>
          <summary>
            {created.length} {created.length === 1 ? 'vendor' : 'vendors'} created
          </summary>
          <ul aria-label="Vendors created">
            {created.map((row) => {
              const name = nameOf(row.row_ref);
              return (
                <li key={row.row_ref}>
                  <code>{row.party_key}</code> — {rowLabel(row.row_ref)}
                  {name === null ? null : <> · {name}</>}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}

      {shared.length > 0 ? (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>Names now held by more than one party</strong>
          </p>
          <ul aria-label="Names shared with another party">
            {shared.map((alias) => (
              <li key={`${alias.row_ref}-${alias.text}-${alias.other_party_key}`}>
                {individual.has(alias.row_ref) ? 'This person’s name' : alias.text} — also held by{' '}
                <code>{alias.other_party_key}</code> ({rowLabel(alias.row_ref)})
              </li>
            ))}
          </ul>
          <p className="mut">
            At intake such a name picks no party: a person chooses. Retire one on Party aliases if it should belong to
            one party only.
          </p>
        </div>
      ) : null}

      {fr29.parties_with_contracts_and_name_changes > 0 ? (
        <div className="banner up" role="status" data-testid="vi-fr29-notice">
          <p className="notice-head">
            <strong>Pseudonymisation maps change for existing contracts</strong>
          </p>
          <p>
            {fr29.parties_with_contracts_and_name_changes} existing{' '}
            {fr29.parties_with_contracts_and_name_changes === 1 ? 'vendor that has' : 'vendors that have'} contracts
            changed names. {fr29.affected_contracts_in_scope}{' '}
            {fr29.affected_contracts_in_scope === 1 ? 'contract' : 'contracts'} in your scope are affected, and contracts
            outside your scope may be affected too.
          </p>
          <ol aria-label="Operator steps">
            {fr29.operator_steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p className="mut">
            The import started none of these. Each is an operator run under its usual approval, and the clause-mining
            refresh is corpus-wide whatever the count in your scope.
          </p>
        </div>
      ) : (
        <p data-testid="vi-fr29-none">{FR29_NO_CHANGE_COPY}</p>
      )}
    </section>
  );
});

function batchCounts(batch: VendorImportBatch): string {
  return [
    `${String(batch.rows_created)} created`,
    `${String(batch.rows_linked)} linked`,
    `${String(batch.rows_updated)} updated`,
    `${String(batch.rows_unchanged)} unchanged`,
    `${String(batch.rows_skipped)} skipped`,
    `${String(batch.rows_rejected)} rejected`,
  ].join(' · ');
}

export function History({
  batches,
  entities,
}: {
  readonly batches: readonly VendorImportBatch[];
  readonly entities: readonly CloudeqEntity[];
}) {
  const columns: readonly DataTableColumn<VendorImportBatch>[] = [
    {
      id: 'when',
      header: 'Imported',
      cell: (batch) => <time dateTime={batch.executed_utc}>{batch.executed_utc}</time>,
    },
    { id: 'batch', header: 'Batch', cell: (batch) => <>#{batch.import_batch_id}</> },
    {
      id: 'entity',
      header: 'cloudEQ company',
      cell: (batch) => (
        <>
          {entityName(entities, batch.cloudeq_entity_key)}
          <span className="mut"> · {ENTITY_SOURCE_COPY[batch.entity_source]}</span>
        </>
      ),
    },
    {
      id: 'file',
      header: 'File',
      cell: (batch) => (
        <>
          {batch.file_name} <span className="mut">({batch.file_format})</span>
        </>
      ),
    },
    { id: 'who', header: 'By', cell: (batch) => batch.executed_by_upn },
    {
      id: 'counts',
      header: 'Rows',
      cell: (batch) => (
        <>
          {batchCounts(batch)}
          <span className="mut"> · {batch.aliases_added} party aliases added</span>
        </>
      ),
    },
  ];
  return (
    <DataTable
      caption="Vendor-list imports, newest first"
      columns={columns}
      rows={batches}
      rowKey={(batch) => batch.import_batch_id}
      empty={<p>No vendor list has been imported yet.</p>}
    />
  );
}
